import type { FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ReelButtonProps {
    children: ReactNode
    href?: string
    onClick?: (event: MouseEvent<HTMLElement>) => void
    variant?: 'outline' | 'solid'
    size?: 'md' | 'sm'
    icon?: 'arrow' | 'external' | 'none'
    external?: boolean
    type?: 'button' | 'submit'
    disabled?: boolean
    noDrag?: boolean
    className?: string
    ariaLabel?: string
}

const ReelButton: FC<ReelButtonProps> = ({
    children,
    href,
    onClick,
    variant = 'outline',
    size = 'md',
    icon = 'arrow',
    external = false,
    type = 'button',
    disabled = false,
    noDrag = false,
    className,
    ariaLabel
}): ReactNode => {
    const classes = cn(
        'group relative inline-flex cursor-pointer select-none items-center overflow-hidden rounded-full border font-medium uppercase backdrop-blur-md',
        'transition-[background-color,border-color,transform,box-shadow,color] duration-300 ease-out',
        'hover:scale-[1.04] hover:shadow-[0_10px_30px_rgba(0,0,0,0.45)] active:scale-[0.99]',
        'disabled:pointer-events-none disabled:opacity-50',
        variant === 'outline' && 'border-white/35 bg-white/[0.04] text-white/95 hover:border-white/85 hover:bg-white/[0.14]',
        variant === 'solid' && 'border-ivory bg-ivory text-ink hover:border-bone hover:bg-bone',
        size === 'md' && 'gap-4 px-8 py-4 text-[12px] tracking-[0.32em] max-sm:gap-3 max-sm:px-6 max-sm:py-3.5 max-sm:text-[11px] max-sm:tracking-[0.26em]',
        size === 'sm' && 'gap-3 px-6 py-3 text-[10.5px] tracking-[0.28em]',
        className
    )

    const content = (
        <>
            <span
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-0 -translate-x-full rounded-full transition-transform duration-500 ease-out group-hover:translate-x-0',
                    variant === 'outline'
                        ? 'bg-linear-to-r from-white/[0.02] via-white/10 to-white/[0.02]'
                        : 'bg-linear-to-r from-white/0 via-white/40 to-white/0'
                )}
            />

            <span className='relative'>{children}</span>

            {icon === 'arrow' && (
                <span
                    aria-hidden
                    className='relative text-[15px] leading-none transition-transform duration-300 ease-out group-hover:translate-x-1.5 max-sm:text-[13px]'
                >
                    →
                </span>
            )}

            {icon === 'external' && (
                <ArrowUpRight
                    aria-hidden
                    className='relative size-4 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5'
                    strokeWidth={1.5}
                />
            )}
        </>
    )

    const dragProps = noDrag ? { 'data-no-drag': '' } : {}

    if (href && external) {
        return (
            <a href={href} target='_blank' rel='noreferrer' className={classes} aria-label={ariaLabel} onClick={onClick} {...dragProps}>
                {content}
            </a>
        )
    }

    if (href) {
        return (
            <Link href={href} className={classes} aria-label={ariaLabel} onClick={onClick} {...dragProps}>
                {content}
            </Link>
        )
    }

    return (
        <button type={type} className={classes} aria-label={ariaLabel} onClick={onClick} disabled={disabled} {...dragProps}>
            {content}
        </button>
    )
}

export default ReelButton
